import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '@/context';
import { useSavedActions } from '@/hooks/useSavedActions';
import type { FlowId, GuidedFlowDefinition } from '@/flows/types';
import type { StepDef } from '@/components/workflow/Stepper';
import type { GuidedFlowRuntime } from '@/flows/runtime/types';
import type { GuidedFlowState } from '@/types/guidedFlow';
import { getFlowDefinition } from '@/flows/registry';
import { getFlowRuntime } from '@/flows/runtime/registry';
import { useGuidedFlow } from '@/hooks/useGuidedFlow';
import { useChatPanelScroll } from '@/hooks/useChatPanelScroll';
import { WORKFLOW_QUERY } from '@/utils/constants';
import {
  isAccessGroupCreationConversation,
  isBulkUserImportConversation,
  isDeviceSetupConversation,
  isUserCreationConversation,
} from '@/utils/conversationWorkflow';
import {
  peekConversationAttachmentMeta,
  toMessageAttachment,
} from '@/utils/conversationAttachment';
import type { BulkUserImportDraft, BulkUserImportSuccess } from '@/types/bulkUserImport';
import { BulkImportFlowArtifact } from '@/components/bulk-import/BulkImportFlowArtifact';
import {
  BulkCredentialIssueFollowUp,
} from '@/components/bulk-import/BulkCredentialIssueFollowUp';
import { staticDefaults } from '@/config/static/catalog';
import {
  bulkUserSuccessBanner,
  bulkUserSuccessIntro,
  bulkUserSuccessNote,
} from '@/config/static/catalog/bulk';
import { BulkReviewSurface } from '@/flows/surfaces/BulkReviewSurface';
import { isFormSurface, resolveSurface } from '@/flows/surfaces/resolveSurface';
import { useBulkUserImportPanel } from '@/hooks/useBulkUserImportPanel';
import { Card } from '@/components/ui';
import { ChatInput } from '@/components/Workspace/ChatInput';
import { GuidedWorkflowPanel } from '@/components/workflow';
import { SparkleIcon } from '@/components/icons';
import { ApprovalSummary, ExplainabilityPanel, FormRenderer, SuccessOutcome } from '@/components/form-kit';
import { siteScopedAssistantHint } from '@/utils/flowAssistantHint';
import { hasFlowApproveMessage } from '@/utils/flowConfirmCta';

interface GuidedConversationPageProps {
  flowId: FlowId;
}

type FlowDraft = Record<string, unknown>;
type FlowResult = {
  summaryLine: string;
  nextSteps: { id: string; label: string }[];
  [key: string]: unknown;
};
type FlowRuntime = GuidedFlowRuntime<FlowDraft, string, FlowResult>;
type FlowState = GuidedFlowState<FlowDraft, string, FlowResult>;
type FlowDefinition = GuidedFlowDefinition<FlowDraft, string, FlowResult>;

function decodeDraftParam(encoded: string | null): string {
  if (!encoded) return '';
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
}

export default function GuidedConversationPage({ flowId }: GuidedConversationPageProps) {
  const { conversationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { effectiveRole, effectiveUser } = useWorkspace();
  const { actions: savedActions, pinTemplate, isTemplatePinned } = useSavedActions(effectiveRole);

  const workflowParam = searchParams.get('workflow');
  const draftEncoded = searchParams.get('draft') ?? searchParams.get('pendingSend');
  const enabled = Boolean(conversationId) && (() => {
    if (flowId === 'device-setup') {
      return workflowParam === WORKFLOW_QUERY.DEVICE_SETUP || isDeviceSetupConversation(conversationId!);
    }
    if (flowId === 'user-creation') {
      return workflowParam === WORKFLOW_QUERY.USER_CREATION || isUserCreationConversation(conversationId!);
    }
    if (flowId === 'bulk-user-import') {
      return workflowParam === WORKFLOW_QUERY.BULK_USER_IMPORT || isBulkUserImportConversation(conversationId!);
    }
    return (
      workflowParam === WORKFLOW_QUERY.ACCESS_GROUP_CREATION ||
      isAccessGroupCreationConversation(conversationId!)
    );
  })();

  const urlDraft = useMemo(() => decodeDraftParam(draftEncoded), [draftEncoded]);
  const [composerDraft] = useState('');
  const [draftInjectKey] = useState(0);
  const consumedDraftUrl = useRef(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const runtime = getFlowRuntime(flowId);
  const definition = getFlowDefinition(flowId);

  const workflow = useGuidedFlow(runtime as unknown as FlowRuntime, {
    conversationId,
    enabled,
    siteLabel: effectiveUser.site,
    userRole: effectiveRole,
    createdByEmail: effectiveUser.email,
  });

  const submitRef = useRef(workflow.submitUserMessage);
  submitRef.current = workflow.submitUserMessage;

  const bulkDraftEarly =
    workflow.state && flowId === 'bulk-user-import'
      ? ((workflow.state as FlowState).draft as unknown as BulkUserImportDraft)
      : null;

  const bulkPanel = useBulkUserImportPanel({
    enabled: flowId === 'bulk-user-import',
    conversationId,
    uiPhase: (workflow.state as FlowState | null)?.uiPhase ?? 'thinking',
    progressRail: (workflow.state as FlowState | null)?.progressRail ?? [],
    bulkDraft: bulkDraftEarly,
    actorEmail: effectiveUser.email,
    updateDraft: workflow.updateDraft,
    appendUserActionMessage: workflow.appendUserActionMessage,
  });

  const postSuccessExchanges = useMemo(() => {
    const s = workflow.state as FlowState | null;
    if (!s || flowId !== 'bulk-user-import' || s.uiPhase !== 'success') return [];

    return s.userMessages
      .filter((message) => message.id.startsWith('action-'))
      .map((message) => {
        const issue = bulkPanel.credentialIssuesByMessageId[message.id];
        const followUp =
          issue && issue.phase !== 'idle' ? (
            <BulkCredentialIssueFollowUp
              phase={issue.phase}
              count={issue.count}
              siteLabel={effectiveUser.site}
            />
          ) : undefined;

        return { message, followUp };
      });
  }, [bulkPanel.credentialIssuesByMessageId, effectiveUser.site, flowId, workflow.state]);

  const chatScrollKey = useMemo(() => {
    const s = workflow.state as FlowState | null;
    if (!s) return 'loading';
    const last = s.userMessages[s.userMessages.length - 1];
    const inProgressStep =
      s.progressRail.find((step) => step.status === 'in-progress')?.id ?? 'done';
    return [
      s.uiPhase,
      s.userMessages.length,
      last?.id ?? '',
      s.statusLabel,
      inProgressStep,
      bulkPanel.credentialIssuePhase,
      bulkPanel.credentialToastCount,
      s.result ? 'result' : '',
    ].join('|');
  }, [bulkPanel.credentialIssuePhase, bulkPanel.credentialToastCount, workflow.state]);

  useChatPanelScroll(bodyRef, chatScrollKey);

  useEffect(() => {
    if (!conversationId || !draftEncoded || !enabled || !workflow.state) return;
    if (consumedDraftUrl.current) return;

    const attachmentMeta = conversationId ? peekConversationAttachmentMeta(conversationId) : null;
    const attachment = attachmentMeta ? toMessageAttachment(attachmentMeta) : undefined;

    if (urlDraft) {
      workflow.stagePendingUserMessage(urlDraft, attachment);
    }

    consumedDraftUrl.current = true;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('draft');
        next.delete('pendingSend');
        if (!next.get('workflow')) next.set('workflow', runtime.workflowQuery);
        return next;
      },
      { replace: true }
    );

    if (urlDraft) {
      queueMicrotask(() =>
        submitRef.current(urlDraft, attachment ? { attachment } : undefined)
      );
    }
  }, [
    conversationId,
    draftEncoded,
    enabled,
    runtime.workflowQuery,
    setSearchParams,
    urlDraft,
    workflow,
  ]);

  if (!conversationId) {
    return (
      <div className="guided-conversation-page">
        <Card className="guided-conversation-page__panel">
          <p className="guided-conversation-page__loading">No active session.</p>
        </Card>
      </div>
    );
  }

  if (!workflow.state) {
    return (
      <div className="guided-conversation-page">
        <Card className="guided-conversation-page__panel">
          <div className="guided-conversation-page__inner">
            <div className="guided-conversation-page__body">
              <p className="guided-conversation-page__loading">{runtime.loadingMessage}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const state = workflow.state as FlowState;
  const aiFilledFields = workflow.aiFilledFields;
  const flowDef = definition as unknown as FlowDefinition;
  const formStepSchema = flowDef.schemas[state.formStep];
  const disableNext = (
    runtime.isStepNextDisabled as unknown as (step: string, draft: FlowDraft) => boolean
  )(state.formStep, state.draft);
  const showExplainability = runtime.shouldShowExplainability(effectiveRole, state.uiPhase);
  const hasConfirmStep = flowDef.steps.some((s) => s.id === 'confirm');
  const exclusiveSaving = Boolean(flowDef.exclusiveSavingScreen);
  const surface = resolveSurface({
    flowId,
    uiPhase: state.uiPhase,
    formStep: state.formStep,
    formSurface: flowDef.formSurface,
    exclusiveSavingScreen: exclusiveSaving,
    hasConfirmStep,
  });
  const isBulkImport = flowId === 'bulk-user-import';
  const explainabilityPanel = showExplainability ? (
    <ExplainabilityPanel
      text={state.explainabilityText}
      expanded={state.showExplainability}
      onToggle={workflow.toggleExplainability}
    />
  ) : null;
  const scopedAssistantHint = siteScopedAssistantHint(runtime.formMessageAssistantHint, effectiveUser.site);
  const formPhaseAssistantHint = state.assistantHint?.trim() || scopedAssistantHint;
  const openingAssistantHint = state.introAssistantHint?.trim() || scopedAssistantHint;

  const bulkDraftForPanel = bulkPanel.draftForPanel;
  const showFormPhase = state.uiPhase === 'form';
  const showStandardForm = showFormPhase && isFormSurface(surface);
  const showIntroPhase =
    state.uiPhase === 'form' ||
    (state.uiPhase === 'saving' && !exclusiveSaving) ||
    state.uiPhase === 'success';

  const showSplitLayout = bulkPanel.splitLayoutOpen;

  const handleFlowConfirm = () => void workflow.approveAndExecute();
  const flowApproveRecorded = hasFlowApproveMessage(state.userMessages);

  const showBulkLeftIntro =
    isBulkImport &&
    bulkPanel.panelAvailable &&
    (state.uiPhase === 'form' ||
      state.uiPhase === 'saving' ||
      (bulkPanel.noCsvUploadVisible &&
        (state.uiPhase === 'working' || state.uiPhase === 'almost-ready')));

  const showBulkPersistedIntro =
    isBulkImport &&
    bulkPanel.panelAvailable &&
    (state.uiPhase === 'form' ||
      state.uiPhase === 'saving' ||
      state.uiPhase === 'success' ||
      (bulkPanel.noCsvUploadVisible &&
        (state.uiPhase === 'working' || state.uiPhase === 'almost-ready')));

  const showBulkArtifact = showBulkPersistedIntro;

  const bulkArtifact = showBulkArtifact ? (
    <BulkImportFlowArtifact
      systemLabel={effectiveUser.site}
      noCsv={!bulkDraftForPanel?.rows.length && !bulkPanel.csvProcessing}
      onOpen={!bulkPanel.rightPanelOpen ? () => bulkPanel.setRightPanelOpen(true) : undefined}
    />
  ) : undefined;

  const bulkSuccessResult = isBulkImport ? (state.result as BulkUserImportSuccess | null) : null;
  const bulkSuccessDefinition =
    bulkSuccessResult != null
      ? {
          ...flowDef.success,
          bannerText: () => bulkUserSuccessBanner(bulkSuccessResult.addedCount, effectiveUser.site),
          noteText: bulkUserSuccessNote(
            bulkSuccessResult.phoneSetupCount,
            bulkSuccessResult.cardOrPinCount
          ),
        }
      : flowDef.success;

  const workflowPanel = (
    <GuidedWorkflowPanel
      uiPhase={state.uiPhase}
      statusLabel={workflow.statusLabel}
      userMessages={state.userMessages}
      onPinMessage={pinTemplate}
      isMessagePinned={isTemplatePinned}
      progressRail={state.progressRail}
      idleHint={runtime.idleHint}
      savingTitle={runtime.savingTitle}
      exclusiveSavingScreen={exclusiveSaving}
      explainability={
        showIntroPhase && state.uiPhase !== 'success' && showExplainability
          ? explainabilityPanel
          : null
      }
      assistantHint={
        showBulkLeftIntro
          ? bulkPanel.noCsvUploadVisible
            ? staticDefaults.bulk.noCsvAssistantHint
            : state.assistantHint || staticDefaults.bulk.assistantHint
          : showIntroPhase && state.uiPhase !== 'success'
            ? formPhaseAssistantHint
            : undefined
      }
      introArtifact={
        showBulkArtifact && state.uiPhase !== 'success' ? bulkArtifact : undefined
      }
      persistIntroDuringSaving={!exclusiveSaving}
      suppressSavingSkeleton={isBulkImport}
      successIntro={
        state.uiPhase === 'success'
          ? {
              explainability: explainabilityPanel,
              priorAssistantHint: showBulkArtifact
                ? state.assistantHint || staticDefaults.bulk.assistantHint
                : openingAssistantHint,
              introArtifact: showBulkArtifact ? bulkArtifact : undefined,
              introAssistantHint: isBulkImport
                ? bulkUserSuccessIntro(effectiveUser.site)
                : state.assistantHint,
            }
          : undefined
      }
      form={
        showStandardForm
          ? {
              title: flowDef.formTitle,
              systemLabel: `${flowDef.systemLabelPrefix}: ${effectiveUser.site}`,
              steps: flowDef.steps as ReadonlyArray<StepDef<string>>,
              activeStep: state.formStep,
              onStepClick: workflow.goToFormStep,
              hideStepper: flowDef.hideStepper,
              compactFormCard: flowDef.compactFormCard,
              persistDuringSaving: flowDef.persistFormDuringSaving,
              headerExtra: state.showAiBanner ? (
                <div className="ai-banner">
                  <SparkleIcon size={18} />
                  <span className="ai-banner__text">
                    {staticDefaults.shared.operatorAiPrefillBanner}
                  </span>
                  <button
                    type="button"
                    className="ai-banner__dismiss"
                    onClick={workflow.dismissAiBanner}
                    aria-label="Dismiss"
                  >
                    ✕
                  </button>
                </div>
              ) : null,
              content:
                state.formStep === 'confirm' ? (
                  <ApprovalSummary
                    draft={state.draft}
                    rows={flowDef.approvalRows as never}
                    aiFilledFields={aiFilledFields}
                    onBack={workflow.prevFormStep}
                    onApprove={handleFlowConfirm}
                    disabled={!workflow.isConfirmReady}
                    showApproveButton={!flowApproveRecorded}
                  />
                ) : (
                  <FormRenderer
                    schema={formStepSchema}
                    role={effectiveRole}
                    draft={state.draft}
                    aiFilledFields={aiFilledFields}
                    showAiBanner={state.showAiBanner}
                    onDraftChange={workflow.updateDraft}
                    onBack={workflow.prevFormStep}
                    onNext={
                      hasConfirmStep
                        ? workflow.nextFormStep
                        : handleFlowConfirm
                    }
                    disableNext={disableNext}
                    disabled={state.uiPhase === 'saving'}
                    saving={state.uiPhase === 'saving'}
                  />
                ),
            }
          : undefined
      }
      success={
        state.uiPhase === 'success' && state.result ? (
          <SuccessOutcome
            draft={state.draft}
            result={state.result}
            definition={isBulkImport ? bulkSuccessDefinition : flowDef.success}
          />
        ) : null
      }
      postSuccessExchanges={postSuccessExchanges}
    />
  );

  const composer = (
    <footer
      className={`guided-conversation-page__composer${state.uiPhase === 'saving' ? ' guided-conversation-page__composer--processing' : ''}`}
    >
      <ChatInput
        variant="flow"
        showTabs={false}
        userSite={effectiveUser.site}
        savedActions={savedActions}
        draftText={composerDraft}
        draftInjectKey={draftInjectKey}
        onSend={(msg, opts) => {
          void workflow.submitUserMessage(msg, { attachmentFile: opts?.attachment ?? null });
        }}
        disabled={state.uiPhase === 'saving'}
      />
    </footer>
  );

  return (
    <div className={`guided-conversation-page${showSplitLayout ? ' guided-conversation-page--split' : ''}`}>
      <Card className="guided-conversation-page__panel">
        <div className="guided-conversation-page__inner">
          {isBulkImport ? (
            showSplitLayout ? (
              <div className="guided-conversation-page__split">
                <div className="guided-conversation-page__split-left">
                  <div className="guided-conversation-page__body" ref={bodyRef}>
                    {workflowPanel}
                  </div>
                  {composer}
                </div>
                <div className="guided-conversation-page__split-right">
                  {bulkDraftForPanel ? (
                    <BulkReviewSurface
                      mode={bulkPanel.rightPanelMode}
                      draft={bulkDraftForPanel}
                      systemLabel={effectiveUser.site}
                      readOnly={bulkPanel.tableReadOnly}
                      tableMode={state.uiPhase === 'success' ? 'post-import' : 'review'}
                      uploadError={bulkPanel.uploadError}
                      showApprove={state.uiPhase === 'form'}
                      approveDisabled={!workflow.isConfirmReady}
                      credentialIssuePhase={bulkPanel.credentialIssuePhase}
                      credentialToastCount={bulkPanel.credentialToastCount}
                      onUploadFile={(file) => void bulkPanel.handleUpload(file)}
                      onUploadError={bulkPanel.setUploadError}
                      onRowUpdate={bulkPanel.handleRowUpdate}
                      onApprove={() => void workflow.approveAndExecute()}
                      onDismissCredentialToast={() => bulkPanel.setCredentialToastCount(null)}
                      onIssueAllMobile={bulkPanel.handleIssueAllMobile}
                      onIssueMobile={bulkPanel.handleIssueSingleMobile}
                      onClosePanel={() => bulkPanel.setRightPanelOpen(false)}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                <div className="guided-conversation-page__body" ref={bodyRef}>
                  {workflowPanel}
                </div>
                {composer}
              </>
            )
          ) : (
            <>
              <div className="guided-conversation-page__body" ref={bodyRef}>
                {workflowPanel}
              </div>
              {composer}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
